import {
  deriveLocationName,
  deriveLocationTypeLabel,
} from './worldLibrary.js';
import type {
  ConditionalAnswer,
  ConnectedAnswers,
  DailyDeviceSelection,
  HistoryEventRecord,
  OperatingSchedule,
  PlotDevice,
  SummaryRecord,
  TimePeriod,
  WorldClock,
  WorldDraft,
  WorldSnapshot,
  WizardStepId,
} from '../domain/types.js';
import {
  parseConditionalAnswer,
  parseDailyDeviceSelection,
  parseHistoryEventRecord,
  parseOperatingSchedule,
  parsePlotDevice,
  parseSummaryRecord,
  parseWorldClock,
  parseWorldDraft,
  parseWorldSnapshot,
} from '../domain/parse.js';
import {
  canBuildLaunchSnapshot,
  confirmedAnswersForSnapshot,
} from '../domain/draftInvalidation.js';
import {
  assertPersistable,
  ForbiddenPersistenceKeyError,
} from '../domain/persistenceGuard.js';
import { DAILY_DEVICE_SELECTION_SCHEMA_VERSION } from '../constants.js';
import { WORLD_DRAFT_SCHEMA_VERSION } from '../domain/types.js';
import { trimGrounding } from '../ui/CreateWorld/questionSchema.js';

export { ForbiddenPersistenceKeyError };

export interface WorldDraftRow {
  draft_id: string;
  schema_version: string;
  wizard_step: string;
  draft_json: string;
  revision: number;
  created_at: string;
  updated_at: string;
}

export interface WorldSnapshotRow {
  world_id: string;
  version: number;
  schema_version: string;
  draft_id: string;
  accepted_at: string;
  location_type: string;
  snapshot_json: string;
  operating_status: 'open' | 'closed';
  clock_json: string;
}

export interface WorldAnswerRow {
  draft_id: string;
  question_id: string;
  schema_version: string;
  status: string;
  source: string | null;
  answer_json: string;
  updated_at: string;
}

export interface DailyUsageRow {
  world_id: string;
  in_world_date: string;
  selection_seed: string;
  device_ids_json: string;
  selected_at: string;
}

/** Draft + monotonic revision for save/resume (repository layer; not in WorldDraft contract). */
export interface PersistedDraftRecord {
  draft: WorldDraft;
  revision: number;
}

export interface SaveDraftResult {
  record: PersistedDraftRecord;
  /** Toast contract — persist only; UI owns navigation. */
  toastMessage: string;
}

export type WorldRuntimeState = 'active' | 'closed';

/** Repository contract — SQLite (A4) and in-memory parity share this surface. */
export interface WorldDraftRepository {
  create(draftId?: string): PersistedDraftRecord;
  get(draftId: string): PersistedDraftRecord | undefined;
  save(record: PersistedDraftRecord): SaveDraftResult;
  delete(draftId: string): boolean;
  list(): PersistedDraftRecord[];
}

function parseRowJson(json: string, context: string): unknown {
  try {
    return JSON.parse(json) as unknown;
  } catch {
    throw new Error(`Malformed JSON in ${context}`);
  }
}

function requireParsed<T>(
  result: { ok: true; value: T } | { ok: false; error: string },
  context: string,
): T {
  if (!result.ok) {
    throw new Error(`Invalid ${context}: ${result.error}`);
  }
  return result.value;
}

export function generateDraftId(now = Date.now()): string {
  const stamp = now.toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  return `draft_${stamp}_${rand}`;
}

export function createEmptyWorldDraft(draftId: string, now = new Date().toISOString()): WorldDraft {
  return {
    schemaVersion: WORLD_DRAFT_SCHEMA_VERSION,
    draftId,
    createdAt: now,
    updatedAt: now,
    wizardStep: 'welcome',
    location: null,
    connected: null,
    answers: {},
    timePeriod: null,
    schedule: null,
    characterDrafts: [],
    reviewAccepted: false,
  };
}

export function createWorldDraft(draftId?: string, now = new Date().toISOString()): PersistedDraftRecord {
  const id = draftId ?? generateDraftId(Date.parse(now));
  return { draft: createEmptyWorldDraft(id, now), revision: 1 };
}

export function saveDraftRecord(
  record: PersistedDraftRecord,
  now = new Date().toISOString(),
): SaveDraftResult {
  const nextRevision = record.revision + 1;
  const draft: WorldDraft = {
    ...record.draft,
    wizardStep: record.draft.wizardStep === 'welcome' ? 'saved-draft' : record.draft.wizardStep,
    lastVisitedStep: record.draft.lastVisitedStep,
    updatedAt: now,
  };
  return {
    record: { draft, revision: nextRevision },
    toastMessage: 'Draft saved.',
  };
}

export function transitionDraftToSaved(record: PersistedDraftRecord, now = new Date().toISOString()): PersistedDraftRecord {
  return {
    revision: record.revision,
    draft: {
      ...record.draft,
      wizardStep: 'saved-draft',
      updatedAt: now,
    },
  };
}

export function resumeDraftFromSaved(
  record: PersistedDraftRecord,
  resumeStep: WizardStepId,
  now = new Date().toISOString(),
): PersistedDraftRecord {
  return {
    revision: record.revision,
    draft: {
      ...record.draft,
      wizardStep: resumeStep,
      updatedAt: now,
    },
  };
}

export function closeActiveWorld(snapshot: WorldSnapshot): WorldSnapshot {
  return { ...snapshot, operatingStatus: 'closed' };
}

export function resumeClosedWorld(snapshot: WorldSnapshot): WorldSnapshot {
  return { ...snapshot, operatingStatus: 'open' };
}

function trimTimePeriodForPersist(period: TimePeriod): TimePeriod {
  return {
    present: period.present?.trim() ?? '',
    period: period.period?.trim() ?? '',
    rationale: period.rationale?.trim() ?? '',
  };
}

function trimConnectedForPersist(connected: ConnectedAnswers): ConnectedAnswers {
  const crowd =
    connected.crowd.type === 'other' && connected.crowd.otherDescription !== undefined
      ? { ...connected.crowd, otherDescription: connected.crowd.otherDescription.trim() }
      : connected.crowd;
  const rhythm =
    !connected.rhythm.skipped
    && connected.rhythm.type === 'other'
    && connected.rhythm.otherDescription !== undefined
      ? { ...connected.rhythm, otherDescription: connected.rhythm.otherDescription.trim() }
      : connected.rhythm;
  return {
    ...connected,
    locationType: connected.locationType.trim(),
    description: connected.description.trim(),
    crowd,
    rhythm,
  };
}

function trimAnswersForPersist(
  answers: Record<string, ConditionalAnswer>,
): Record<string, ConditionalAnswer> {
  const out: Record<string, ConditionalAnswer> = {};
  for (const [id, answer] of Object.entries(answers)) {
    if (answer.value === null || typeof answer.value !== 'string') {
      out[id] = answer;
      continue;
    }
    out[id] = { ...answer, value: answer.value.trim() };
  }
  return out;
}

/** Trim user-entered strings at persistence only — in-memory draft keeps raw typing values. */
function trimDraftStringsForPersist(draft: WorldDraft): WorldDraft {
  const schedule =
    draft.schedule?.timezone !== undefined
    && (draft.schedule.timezoneStrategy === 'explicit' || draft.schedule.timezoneStrategy === 'location')
      ? { ...draft.schedule, timezone: draft.schedule.timezone?.trim() }
      : draft.schedule;
  return {
    ...draft,
    location: draft.location ? trimGrounding(draft.location) : null,
    timePeriod: draft.timePeriod ? trimTimePeriodForPersist(draft.timePeriod) : null,
    connected: draft.connected ? trimConnectedForPersist(draft.connected) : null,
    answers: trimAnswersForPersist(draft.answers),
    schedule,
  };
}

function libraryLocationLabel(snapshot: WorldSnapshot): string {
  return deriveLocationTypeLabel(snapshot.connected) || deriveLocationName(snapshot.location);
}

export function serializeWorldDraft(
  record: PersistedDraftRecord | WorldDraft,
  revision = 1,
): WorldDraftRow {
  const raw = 'draft' in record ? record.draft : record;
  const draft = trimDraftStringsForPersist(raw);
  const rev = 'revision' in record ? record.revision : revision;
  assertPersistable(draft);
  return {
    draft_id: draft.draftId,
    schema_version: draft.schemaVersion,
    wizard_step: draft.lastVisitedStep ?? draft.wizardStep,
    draft_json: JSON.stringify(draft),
    revision: rev,
    created_at: draft.createdAt,
    updated_at: draft.updatedAt,
  };
}

export function deserializeWorldDraft(row: WorldDraftRow): PersistedDraftRecord {
  const raw = parseRowJson(row.draft_json, `world draft row ${row.draft_id}`);
  const draft = requireParsed(parseWorldDraft(raw), `world draft row ${row.draft_id}`) as WorldDraft;
  return { draft, revision: row.revision ?? 1 };
}

export function serializeWorldSnapshot(snapshot: WorldSnapshot): WorldSnapshotRow {
  const nonConfirmed = Object.entries(snapshot.answers).filter(
    ([, answer]) => answer.status !== 'confirmed',
  );
  if (nonConfirmed.length > 0) {
    const ids = nonConfirmed.map(([id, answer]) => `${id}(${answer.status})`).join(', ');
    throw new Error(
      `Cannot serialize world snapshot ${snapshot.worldId}: non-confirmed answers present: ${ids}`,
    );
  }
  const persistable: WorldSnapshot = {
    ...snapshot,
    answers: confirmedAnswersForSnapshot(snapshot.answers),
  };
  assertPersistable(persistable);
  const parsed = parseWorldSnapshot(persistable);
  if (!parsed.ok) {
    throw new Error(`Cannot serialize world snapshot ${snapshot.worldId}: ${parsed.error}`);
  }
  return {
    world_id: snapshot.worldId,
    version: snapshot.version,
    schema_version: snapshot.schemaVersion,
    draft_id: snapshot.draftId,
    accepted_at: snapshot.acceptedAt,
    location_type: libraryLocationLabel(snapshot),
    snapshot_json: JSON.stringify(parsed.value),
    operating_status: snapshot.operatingStatus,
    clock_json: JSON.stringify(snapshot.clock),
  };
}

export function deserializeWorldSnapshot(row: WorldSnapshotRow): WorldSnapshot {
  const raw = parseRowJson(row.snapshot_json, `world snapshot row ${row.world_id}`);
  return requireParsed(
    parseWorldSnapshot(raw),
    `world snapshot row ${row.world_id}`,
  ) as WorldSnapshot;
}

export function serializeConditionalAnswer(
  draftId: string,
  answer: ConditionalAnswer,
): WorldAnswerRow {
  assertPersistable(answer);
  return {
    draft_id: draftId,
    question_id: answer.questionId,
    schema_version: answer.schemaVersion,
    status: answer.status,
    source: answer.source,
    answer_json: JSON.stringify(answer),
    updated_at: answer.updatedAt,
  };
}

export function deserializeConditionalAnswer(row: WorldAnswerRow): ConditionalAnswer {
  const raw = parseRowJson(row.answer_json, `answer row ${row.draft_id}/${row.question_id}`);
  return requireParsed(
    parseConditionalAnswer(raw),
    `answer row ${row.draft_id}/${row.question_id}`,
  ) as ConditionalAnswer;
}

export function serializeOperatingSchedule(schedule: OperatingSchedule): string {
  assertPersistable(schedule);
  const parsed = parseOperatingSchedule(schedule);
  if (!parsed.ok) {
    throw new Error(`Cannot serialize operating schedule: ${parsed.error}`);
  }
  return JSON.stringify(parsed.value);
}

export function deserializeOperatingSchedule(json: string): OperatingSchedule {
  const raw = parseRowJson(json, 'operating schedule');
  return requireParsed(parseOperatingSchedule(raw), 'operating schedule') as OperatingSchedule;
}

export function serializePlotDevice(device: PlotDevice): string {
  assertPersistable(device);
  const parsed = parsePlotDevice(device);
  if (!parsed.ok) {
    throw new Error(`Cannot serialize plot device ${device.deviceId}: ${parsed.error}`);
  }
  return JSON.stringify(parsed.value);
}

export function deserializePlotDevice(json: string): PlotDevice {
  const raw = parseRowJson(json, 'plot device');
  return requireParsed(parsePlotDevice(raw), 'plot device') as PlotDevice;
}

export function serializeDailyUsage(selection: DailyDeviceSelection): DailyUsageRow {
  assertPersistable(selection);
  const parsed = parseDailyDeviceSelection(selection);
  if (!parsed.ok) {
    throw new Error(`Cannot serialize daily device usage: ${parsed.error}`);
  }
  return {
    world_id: selection.worldId,
    in_world_date: selection.inWorldDate,
    selection_seed: selection.selectionSeed,
    device_ids_json: JSON.stringify(parsed.value.deviceIds),
    selected_at: selection.selectedAt,
  };
}

export function deserializeDailyUsage(row: DailyUsageRow): DailyDeviceSelection {
  let deviceIds: unknown;
  try {
    deviceIds = JSON.parse(row.device_ids_json);
  } catch {
    throw new Error(`Malformed JSON in daily usage row ${row.world_id}/${row.in_world_date}`);
  }
  return requireParsed(
    parseDailyDeviceSelection({
      schemaVersion: DAILY_DEVICE_SELECTION_SCHEMA_VERSION,
      worldId: row.world_id,
      inWorldDate: row.in_world_date,
      selectionSeed: row.selection_seed,
      deviceIds,
      selectedAt: row.selected_at,
    }),
    `daily usage row ${row.world_id}/${row.in_world_date}`,
  ) as DailyDeviceSelection;
}

export function serializeSummaryRecord(summary: SummaryRecord): string {
  assertPersistable(summary);
  const parsed = parseSummaryRecord(summary);
  if (!parsed.ok) {
    throw new Error(`Cannot serialize summary ${summary.summaryId}: ${parsed.error}`);
  }
  return JSON.stringify(parsed.value);
}

export function deserializeSummaryRecord(json: string): SummaryRecord {
  const raw = parseRowJson(json, 'summary record');
  return requireParsed(parseSummaryRecord(raw), 'summary record') as SummaryRecord;
}

export function serializeHistoryEvent(event: HistoryEventRecord): string {
  assertPersistable(event);
  const parsed = parseHistoryEventRecord(event);
  if (!parsed.ok) {
    throw new Error(`Cannot serialize history event ${event.eventId}: ${parsed.error}`);
  }
  return JSON.stringify(parsed.value);
}

export function deserializeHistoryEvent(json: string): HistoryEventRecord {
  const raw = parseRowJson(json, 'history event');
  return requireParsed(parseHistoryEventRecord(raw), 'history event') as HistoryEventRecord;
}

export function deserializeWorldClock(json: string): WorldClock {
  const raw = parseRowJson(json, 'world clock');
  return requireParsed(parseWorldClock(raw), 'world clock') as WorldClock;
}

/** In-memory repository for browser dev parity (A4 wires SQLite to same contract). */
export class InMemoryWorldDraftRepository implements WorldDraftRepository {
  private readonly records = new Map<string, PersistedDraftRecord>();

  create(draftId?: string): PersistedDraftRecord {
    const record = createWorldDraft(draftId);
    this.records.set(record.draft.draftId, record);
    return record;
  }

  get(draftId: string): PersistedDraftRecord | undefined {
    return this.records.get(draftId);
  }

  save(record: PersistedDraftRecord): SaveDraftResult {
    const result = saveDraftRecord(record);
    this.records.set(result.record.draft.draftId, result.record);
    return result;
  }

  delete(draftId: string): boolean {
    return this.records.delete(draftId);
  }

  list(): PersistedDraftRecord[] {
    return [...this.records.values()].sort((a, b) =>
      b.draft.updatedAt.localeCompare(a.draft.updatedAt),
    );
  }
}

export { canBuildLaunchSnapshot };
export * from './worldLibrary.js';
